import { supabase, VentaActividad } from '../lib/supabase';
import { solicitudActividadService } from './solicitudActividadService';


import type { MedioPago, RespaldoMovimiento } from '../types/comprobante';
import { fechaEnChile } from '../utils/dateHelpers';

function fechaCivilDelPago(fecha?: string): string {
  const fechaPago = fecha || fechaEnChile(new Date());
  if (!fechaPago) throw new Error('No se pudo determinar la fecha del pago en Chile.');
  return fechaPago;
}

interface CrearPedidoData {
  actividad_id: string;
  producto_id?: string;
  produccion_id?: string;
  miembro_id?: string | null;
  nombre_externo?: string | null;
  miembro_referido_id?: string | null;
  cantidad: number;
  monto_total: number;
  forma_entrega: 'retiro' | 'despacho';
  fecha_entrega?: string;
  direccion_despacho?: string;
  rango_horario_retiro_despacho?: string;
  modo_pago: MedioPago;
  fecha_venta: string;
  notas?: string;
  registrar_pago: boolean;
  monto_pago_inicial?: number;
  fecha_pago?: string;
  registrar_entrega: boolean;
  cantidad_entrega_inicial?: number;
  fecha_entrega_real?: string;
  /** Respaldo del pago inicial; obligatorio cuando registrar_pago y hay monto. */
  respaldo_pago?: RespaldoMovimiento;
}

interface RegistrarPagoData {
  monto: number;
  modo_pago: MedioPago;
  fecha?: string;
  notas?: string;
  respaldo: RespaldoMovimiento;
}

interface RegistrarEntregaData {
  cantidad: number;
  fecha?: string;
  notas?: string;
}

export type CambiosVentaActividad = Partial<Pick<VentaActividad,
  'cantidad' | 'monto_total' | 'forma_entrega' | 'modo_pago' | 'fecha_venta'>> &
  Partial<Record<'producto_id' | 'produccion_id' | 'miembro_id' | 'nombre_externo' |
    'miembro_referido_id' | 'fecha_entrega' | 'direccion_despacho' |
    'rango_horario_retiro_despacho' | 'notas', string | null>>;

export const ventaActividadService = {
  async getByActividad(actividadId: string): Promise<VentaActividad[]> {
    const { data, error } = await supabase
      .from('ventas_actividad')
      .select('*')
      .eq('actividad_id', actividadId)
      .eq('anulado', false)
      .order('fecha_venta', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<VentaActividad | null> {
    const { data, error } = await supabase
      .from('ventas_actividad')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async crearPedido(pedidoData: CrearPedidoData): Promise<VentaActividad> {
    const { respaldo_pago, ...original } = pedidoData;
    const datos = {
      ...original,
      producto_id: original.producto_id || null, produccion_id: original.produccion_id || null,
      miembro_id: original.miembro_id || null, nombre_externo: original.nombre_externo || null,
      miembro_referido_id: original.miembro_referido_id || null,
      fecha_pago: original.registrar_pago ? fechaCivilDelPago(original.fecha_pago) : null,
      fecha_entrega_real: original.registrar_entrega ? fechaCivilDelPago(original.fecha_entrega_real) : null,
    };
    const id = crypto.randomUUID(); const movimiento = crypto.randomUUID(); const historial = crypto.randomUUID();
    const respaldo = respaldo_pago ?? {};
    return solicitudActividadService.ejecutarContrato<VentaActividad>('crear_pedido_actividad',
      { p_id: id, p_movimiento: movimiento, p_historial: historial, p_datos: datos, p_respaldo: respaldo },
      { id, movimiento, historial, datos, respaldo });
  },

  async registrarPago(pedidoId: string, pagoData: RegistrarPagoData): Promise<VentaActividad> {
    const datos = { ...pagoData, fecha: fechaCivilDelPago(pagoData.fecha) };
    const movimiento = crypto.randomUUID(); const historial = crypto.randomUUID();
    return solicitudActividadService.ejecutarContrato<VentaActividad>('registrar_pago_pedido_actividad',
      { p_id: pedidoId, p_movimiento: movimiento, p_historial: historial, p_datos: datos },
      { id: pedidoId, movimiento, historial, datos });
  },

  async registrarEntrega(pedidoId: string, entregaData: RegistrarEntregaData): Promise<VentaActividad> {
    const datos = { ...entregaData, fecha: fechaCivilDelPago(entregaData.fecha) };
    const historial = crypto.randomUUID();
    return solicitudActividadService.ejecutarContrato<VentaActividad>('registrar_entrega_pedido_actividad',
      { p_id: pedidoId, p_historial: historial, p_datos: datos }, { id: pedidoId, historial, datos });
  },

  async update(id: string, cambios: CambiosVentaActividad): Promise<VentaActividad> {
    return solicitudActividadService.ejecutarContrato<VentaActividad>('editar_venta',
      { p_id: id, p_cambios: cambios }, { id, cambios });
  },

  async delete(id: string, motivo?: string): Promise<void> {
    const datos = { motivo: motivo || 'Venta anulada', fecha_anulacion: new Date().toISOString() };
    await solicitudActividadService.ejecutarContrato<VentaActividad>('anular_venta_actividad',
      { p_id: id, p_datos: datos }, { id, datos });
  },

  async getTotalIngresosByActividad(actividadId: string): Promise<number> {
    const { data, error } = await supabase
      .from('ventas_actividad')
      .select('monto_pagado')
      .eq('actividad_id', actividadId)
      .eq('anulado', false);

    if (error) throw error;

    return data?.reduce((sum, v) => sum + Number(v.monto_pagado), 0) || 0;
  },

  async getTotalIngresosPendientesByActividad(actividadId: string): Promise<number> {
    const { data, error } = await supabase
      .from('ventas_actividad')
      .select('monto_total, monto_pagado')
      .eq('actividad_id', actividadId)
      .eq('anulado', false)
      .in('estado_pago', ['pendiente', 'pago_parcial']);

    if (error) throw error;

    return data?.reduce((sum, v) => sum + (Number(v.monto_total) - Number(v.monto_pagado)), 0) || 0;
  }
};
